export function openConsentmoPreferences(trigger, provider = globalThis.showPreferences) {
  if (typeof provider !== 'function') return false;
  try {
    provider(trigger);
    return true;
  } catch {
    return false;
  }
}

const PrivacyControlsElement = globalThis.HTMLElement || class {};

class PrivacyControls extends PrivacyControlsElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.querySelector('[data-privacy-preferences]')?.addEventListener('click', event => {
      event.preventDefault();
      const opened = openConsentmoPreferences(event.currentTarget, window.showPreferences);
      const status = this.querySelector('[data-privacy-status]');
      if (status) status.textContent = opened ? '' : this.dataset.unavailableMessage;
    }, { signal: this.controller.signal });
  }

  disconnectedCallback() { this.controller?.abort(); }
}

if (globalThis.customElements && !customElements.get('fl-privacy-controls')) customElements.define('fl-privacy-controls', PrivacyControls);

// Consentmo's accessibility menu opens from the footer Accessibility link (its link mode copies the hidden
// .accessibility-link-slot button). The app's panel does not always take focus when it opens, ignores
// Escape and, after its closing transition (about 0.3 s), drops focus on the page body. So focus moves to
// the panel's first control (Close) when it is still on the link, Escape closes the panel, and focus returns
// to the link. Both are checked until the app's transitions are over, unless the shopper has moved focus.
export const ACCESSIBILITY_FOCUS_CHECKS_MS = [0, 150, 350, 600, 1000];

export function connectAccessibilityMenu(doc, menu = () => globalThis.Consentmo?.accessibility, schedule = globalThis.setTimeout) {
  const controller = new AbortController(), { signal } = controller;
  let opener = null;
  const remember = event => {
    if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
    const link = event.target?.closest?.('.csm-accessibility-link');
    if (link) opener = link;
  };
  doc.addEventListener('click', remember, { capture: true, signal });
  doc.addEventListener('keydown', remember, { capture: true, signal });
  doc.addEventListener('keydown', event => {
    const api = menu();
    if (event.key === 'Escape' && api?.isOpen?.()) api.close();
  }, { signal });
  doc.addEventListener('consentmo:accessibilityOpened', () => {
    const link = opener;
    if (!link) return;
    const enter = () => {
      const buttons = doc.querySelector('csm-web-accessibility')?.shadowRoot?.querySelectorAll('#csm-ada-popover button') ?? [];
      const first = [...buttons].find(button => button.getClientRects().length > 0);
      if (!signal.aborted && opener === link && doc.activeElement === link && menu()?.isOpen?.()) first?.focus();
    };
    for (const delay of ACCESSIBILITY_FOCUS_CHECKS_MS) schedule(enter, delay);
  }, { signal });
  doc.addEventListener('consentmo:accessibilityClosed', () => {
    const link = opener;
    opener = null;
    if (!link) return;
    const restore = () => {
      const active = doc.activeElement;
      if (!signal.aborted && link.isConnected && (!active || active === doc.body || active.localName === 'csm-web-accessibility')) link.focus();
    };
    for (const delay of ACCESSIBILITY_FOCUS_CHECKS_MS) schedule(restore, delay);
  }, { signal });
  return () => controller.abort();
}

if (globalThis.document) connectAccessibilityMenu(globalThis.document);
